import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { PricingTreatment } from "@/lib/quoteChoices";

interface Props {
  leadId: string;
  currentItemId?: string | null;
  treatment: PricingTreatment;
  alternativeTo: string | null;
  onChange: (treatment: PricingTreatment, alternativeTo: string | null) => void;
  linkedOption?: string | null;
  onLinkedOptionChange?: (id: string | null) => void;
}

export function PricingTreatmentField({ leadId, currentItemId, treatment, alternativeTo, onChange, linkedOption, onLinkedOptionChange }: Props) {
  const { data: candidates = [] } = useQuery({
    queryKey: ["itinerary-alt-candidates", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_items")
        .select("id, title, custom_title, item_type, date, check_in, pricing_treatment, alternative_to_item_id")
        .eq("lead_id", leadId)
        .neq("include_in_quote", false);
      if (error) throw error;
      return data || [];
    },
  });
  const primaries = (candidates as any[]).filter(
    (c) => c.id !== currentItemId && (c.pricing_treatment ?? "included") === "included"
  );

  const all = candidates as any[];
  const myGroup = treatment === "alternative" ? alternativeTo : currentItemId;
  const primaryIds = new Set(all.filter((c) => c.pricing_treatment === "alternative" && c.alternative_to_item_id).map((c) => c.alternative_to_item_id));
  const linkOptions = all.filter((c) => {
    if (c.id === currentItemId) return false;
    const group = c.pricing_treatment === "alternative" ? c.alternative_to_item_id : primaryIds.has(c.id) ? c.id : null;
    return group && group !== myGroup;
  });
  const showLink = !!onLinkedOptionChange && treatment !== "optional" && (linkOptions.length > 0 || !!linkedOption);

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label className="text-sm font-medium">Pricing on the quote</Label>
      <ToggleGroup
        type="single"
        variant="outline"
        value={treatment}
        onValueChange={(v) => v && onChange(v as PricingTreatment, v === "alternative" ? alternativeTo : null)}
        className="justify-start"
      >
        <ToggleGroupItem value="included">Included</ToggleGroupItem>
        <ToggleGroupItem value="optional">Optional extra</ToggleGroupItem>
        <ToggleGroupItem value="alternative">Alternative option</ToggleGroupItem>
      </ToggleGroup>
      <p className="text-xs text-muted-foreground">
        {treatment === "included" && "Part of the programme; its cost is in the total."}
        {treatment === "optional" && "Shown as an optional extra. Not in the total — the client can add it on the quote."}
        {treatment === "alternative" && "An alternative to another item (e.g. a different hotel). Not in the total — the client can swap it in on the quote and see the difference."}
      </p>
      {treatment === "alternative" && (
        <Select value={alternativeTo || ""} onValueChange={(v) => onChange("alternative", v)}>
          <SelectTrigger>
            <SelectValue placeholder="Alternative to…" />
          </SelectTrigger>
          <SelectContent>
            {primaries.map((p: any) => (
              <SelectItem key={p.id} value={p.id}>
                {p.custom_title || p.title}
                {(p.check_in || p.date) ? ` · ${p.check_in || p.date}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {showLink && (
        <div className="space-y-1 pt-1">
          <Label className="text-xs">Goes with option…</Label>
          <Select value={linkedOption || "none"} onValueChange={(v) => onLinkedOptionChange?.(v === "none" ? null : v)}>
            <SelectTrigger><SelectValue placeholder="Not linked" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not linked</SelectItem>
              {linkOptions.map((p: any) => (
                <SelectItem key={p.id} value={p.id}>{p.custom_title || p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            When the client picks that option (e.g. a hotel), this item is selected automatically.
          </p>
        </div>
      )}
    </div>
  );
}
